// VenueForm.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getAllStateApi,
  getCityByStateIdApi,
  addVenueApi,
  updateVenueApi,
  getAppSettingsApi,
} from "../../../../src/redux/api/api";

const VenueForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [stateData, setStateData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [mapApiKey, setMapApiKey] = useState("");
  const [cityDisabled, setCityDisabled] = useState(true);

  const center = {
    lat: Number(state?.latitude) || 40.7128,
    lng: Number(state?.longitude) || -74.006,
  };

  const [position, setPosition] = useState(center);
  const autocompleteRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries: ["places"],
  });

  // Handle map click
  const handleMapClick = (event) => {
    const lat = Number(event.latLng.lat());
    const lng = Number(event.latLng.lng());
    if (!isNaN(lat) && !isNaN(lng)) {
      setPosition({ lat, lng });
      setValue("latitude", lat);
      setValue("longitude", lng);
    } else {
      console.error("Invalid lat/lng:", lat, lng);
    }
  };

  // Form submit
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const req = {
        address: data.address,
        stateid: data.state,
        cityid: data.city,
        latitude: data.latitude,
        longitude: data.longitude,
        id: state ? state.id : undefined,
      };
      const apiCall = state ? updateVenueApi(req, state) : addVenueApi(req);
      await apiCall;
      toast.success("Venue saved successfully!");
      navigate("/venue");
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.message || "An error occurred. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch states
  const getStates = async () => {
    try {
      const res = await getAllStateApi();
      if (res.status === 200) {
        setStateData(res.data.info);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load states");
    }
  };

  // Fetch cities
  const getCities = async (stateid) => {
    try {
      const res = await getCityByStateIdApi({ stateid });
      if (res.status === 200) {
        setCityData(res.data.info);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load cities");
    }
  };

  // Fetch Map API key
  const getMapApiKey = async () => {
    try {
      const res = await getAppSettingsApi();
      if (res.status === 200) {
        setMapApiKey(res.data.info.map_api_key);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load Map API key");
    }
  };

  // Load states + map key
  useEffect(() => {
    getStates();
    getMapApiKey();
  }, []);

  // Pre-fill form if editing
  useEffect(() => {
    if (state) {
      setValue("address", state.address);
      setValue("state", state.stateid);
      setValue("city", state.cityid);
      setValue("latitude", state.latitude);
      setValue("longitude", state.longitude);
      if (state.stateid) {
        getCities(state.stateid);
        setCityDisabled(false);
      }
    }
  }, [state, setValue]);

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  useEffect(() => {
    if (latitude && longitude) {
      setPosition({ lat: Number(latitude), lng: Number(longitude) });
    }
  }, [latitude, longitude]);

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Venue Form" />
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Venue Name"
                      {...register("address", { required: "Venue Name is required" })}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Select
                      fullWidth
                      displayEmpty
                      value={getValues("state") || ""}
                      {...register("state", { required: "State is required" })}
                      onChange={(e) => {
                        setValue("state", e.target.value);
                        clearErrors("state");
                        if (e.target.value) {
                          setCityDisabled(false);
                          getCities(e.target.value);
                        } else {
                          setCityDisabled(true);
                          setValue("city", "");
                        }
                      }}
                      error={!!errors.state}
                    >
                      <MenuItem value="">Select State</MenuItem>
                      {stateData.map((item) => (
                        <MenuItem key={item._id} value={item._id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.state && (
                      <Typography color="error" variant="caption">
                        {errors.state.message}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Select
                      fullWidth
                      displayEmpty
                      disabled={cityDisabled}
                      value={getValues("city") || ""}
                      {...register("city", { required: "City is required" })}
                      onChange={(e) => {
                        setValue("city", e.target.value);
                        clearErrors("city");
                      }}
                      error={!!errors.city}
                    >
                      <MenuItem value="">Select City</MenuItem>
                      {cityData.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.city && (
                      <Typography color="error" variant="caption">
                        {errors.city.message}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Latitude"
                      {...register("latitude", { required: "Latitude is required" })}
                      error={!!errors.latitude}
                      helperText={errors.latitude?.message}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Longitude"
                      {...register("longitude", { required: "Longitude is required" })}
                      error={!!errors.longitude}
                      helperText={errors.longitude?.message}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "400px" }}
                        center={position}
                        zoom={14}
                        onClick={handleMapClick}
                      >
                        <Marker position={position} />
                      </GoogleMap>
                    ) : (
                      <Typography>Loading map...</Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} textAlign="center">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      startIcon={isLoading && <CircularProgress size={20} />}
                    >
                      {isLoading ? "Submitting..." : "Submit"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenueForm;
